import type { DiskMetric } from '@/types/monitoring'

const ignoredFilesystems = new Set([
  'block',
  'autofs',
  'binfmt_misc',
  'bpf',
  'configfs',
  'debugfs',
  'devpts',
  'squashfs',
  'tmpfs',
  'devtmpfs',
  'efivarfs',
  'fusectl',
  'hugetlbfs',
  'mqueue',
  'pstore',
  'proc',
  'rpc_pipefs',
  'securityfs',
  'sysfs',
  'cgroup',
  'cgroup2',
  'overlay',
  'ramfs',
  'tracefs',
  'nfs',
  'nfs4',
  'cifs',
  'smb3',
  '9p',
  'ceph',
  'glusterfs',
  'fuse.sshfs',
])

function ignoredDevice(device: string): boolean {
  const name = device.split('/').pop()?.toLowerCase() || ''
  return /^(loop|ram|zram|nbd|rbd)\d/.test(name)
}

export function aggregateDisks(disks: DiskMetric[]): DiskMetric[] {
  const reportedAggregate = disks.filter(
    (disk) => disk.filesystem === 'aggregate' && disk.totalBytes > 0,
  )
  const candidates = reportedAggregate.length
    ? reportedAggregate
    : disks.filter(
        (disk) =>
          disk.totalBytes > 0 &&
          !ignoredFilesystems.has(disk.filesystem.toLowerCase()) &&
          !ignoredDevice(disk.device),
      )

  const unique = new Map<string, DiskMetric>()
  for (const disk of candidates) {
    const key = reportedAggregate.length ? disk.mountpoint : disk.device || disk.mountpoint
    if (!unique.has(key)) unique.set(key, disk)
  }
  if (!unique.size) return []

  let totalBytes = 0
  let usedBytes = 0
  let readBytesPerSecond = 0
  let writeBytesPerSecond = 0
  let readIops = 0
  let writeIops = 0
  for (const disk of unique.values()) {
    totalBytes += Math.max(0, disk.totalBytes)
    usedBytes += Math.max(0, disk.usedBytes)
    readBytesPerSecond += Math.max(0, disk.readBytesPerSecond)
    writeBytesPerSecond += Math.max(0, disk.writeBytesPerSecond)
    readIops += Math.max(0, disk.readIops)
    writeIops += Math.max(0, disk.writeIops)
  }
  usedBytes = Math.min(usedBytes, totalBytes)

  return [
    {
      device: 'physical',
      mountpoint: '[aggregate]',
      filesystem: 'aggregate',
      totalBytes,
      usedBytes,
      usedPercent: totalBytes ? (usedBytes / totalBytes) * 100 : 0,
      readBytesPerSecond,
      writeBytesPerSecond,
      readIops,
      writeIops,
    },
  ]
}
